import React, { useState, useEffect } from 'react';
import { Container, Spinner, Modal, Button, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { getTenants, saveTenant, getTenantHistory } from '../../services/tenant-service';
import ResponsiveTable from '../Grid/ResponsiveTable';
import InputBox from '../common/InputBox';
import SearchSelect from '../common/SearchSelect';
import { formatDate } from '../../helpers/dateUtils';

const EMPTY_FORM = { tenantId: '', tenantName: '', tenantAdminEmail: '', tenantAdminName: '', status: 'Active' };

const TenantMaster = () => {
  const [loading, setLoading]       = useState(false);
  const [data, setData]             = useState([]);
  const [showModal, setShowModal]   = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [isEdit, setIsEdit]         = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await getTenants();
      if (res.statusCode === '0000') setData(res.data || []);
    } catch { toast.error('Failed to load tenants'); }
    finally { setLoading(false); }
  };

  const handleAdd = () => {
    setForm(EMPTY_FORM);
    setIsEdit(false);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setForm({ tenantId: item.tenantId, tenantName: item.tenantName || '', tenantAdminEmail: item.tenantAdminEmail || '', tenantAdminName: item.tenantAdminName || '', status: item.status });
    setIsEdit(true);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.tenantId) { toast.error('Tenant ID is required'); return; }
    setLoading(true);
    try {
      const res = await saveTenant(form);
      if (res.statusCode === '0000') {
        toast.success(res.statusMsg);
        setShowModal(false);
        loadData();
      } else {
        toast.error(res.statusMsg || 'Failed');
      }
    } catch { toast.error('Failed to save tenant'); }
    finally { setLoading(false); }
  };

  const viewHistory = async (item) => {
    setLoading(true);
    try {
      const res = await getTenantHistory(item.tenantId);
      setHistoryData(res.data || []);
      setShowHistory(true);
    } catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  };

  const handleSwitch = (item) => {
    sessionStorage.setItem('TenantId', item.tenantId);
    window.location.href = '/dashboard';
  };

  const headers = [
    { displayName: 'Tenant ID',          columnName: 'tenantId',        dataType: 'text', width: 150, displayIndication: true },
    { displayName: 'Tenant Name',         columnName: 'tenantName',      dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Admin Email',         columnName: 'tenantAdminEmail', dataType: 'text', width: 250, displayIndication: true },
    { displayName: 'Admin Name',          columnName: 'tenantAdminName',  dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Status',              columnName: 'status',          dataType: 'text', width: 120, displayIndication: true },
    { displayName: 'Modified By',         columnName: 'modifiedBy',      dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Modified On',         columnName: 'modifiedDate',    dataType: 'date', width: 180, displayIndication: true, formatter: formatDate },
  ];

  const icons = (item) => {
    const current = sessionStorage.getItem('TenantId');
    const actions = [];
    if (item.status === 'Active' && item.tenantId !== current) {
      actions.push({ type: 'button', text: 'Switch', click: () => handleSwitch(item) });
    }
    actions.push({ type: 'button', text: 'Edit',    click: () => handleEdit(item) });
    actions.push({ type: 'button', text: 'History', click: () => viewHistory(item) });
    return actions;
  };

  const historyHeaders = [
    { displayName: 'Tenant ID',   columnName: 'tenantId',        dataType: 'text', width: 150, displayIndication: true },
    { displayName: 'Tenant Name', columnName: 'tenantName',      dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Admin Email', columnName: 'tenantAdminEmail', dataType: 'text', width: 220, displayIndication: true },
    { displayName: 'Status',      columnName: 'status',          dataType: 'text', width: 100, displayIndication: true },
    { displayName: 'Modified By', columnName: 'modifiedBy',      dataType: 'text', width: 200, displayIndication: true },
    { displayName: 'Modified On', columnName: 'modifiedDate',    dataType: 'date', width: 180, displayIndication: true, formatter: formatDate },
  ];

  return (
    <>
      {loading && <div className="spinner-box"><Spinner animation="border" className="spinner" /></div>}
      <Container bsPrefix="main-body">
        <div className="page-title">Tenant Master</div>
        <div className="page-sub-title">Manage deployment tenants and their administrators</div>
        <ResponsiveTable
          headers={headers} data={data} icons={icons}
          dataType="Tenants" button="Tenant" handleAdd={handleAdd}
          sessionName="Tenant Master" noCheckbox={true} pageSize={10}
          enableDownload={true} forceWriteAccess={true}
        />
      </Container>

      <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'Edit Tenant' : 'Add Tenant'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col md={12}>
              <InputBox type="text" label="Tenant ID" name="tenantId" value={form.tenantId}
                onChange={(e) => setForm((p) => ({ ...p, tenantId: e.target.value }))}
                disabled={isEdit} required={true} />
            </Col>
            <Col md={12}>
              <InputBox type="text" label="Tenant Name" name="tenantName" value={form.tenantName}
                onChange={(e) => setForm((p) => ({ ...p, tenantName: e.target.value }))} />
            </Col>
            <Col md={12}>
              <InputBox type="text" label="Admin Email" name="tenantAdminEmail" value={form.tenantAdminEmail}
                onChange={(e) => setForm((p) => ({ ...p, tenantAdminEmail: e.target.value }))} />
            </Col>
            <Col md={12}>
              <InputBox type="text" label="Admin Name" name="tenantAdminName" value={form.tenantAdminName}
                onChange={(e) => setForm((p) => ({ ...p, tenantAdminName: e.target.value }))} />
            </Col>
            <Col md={12}>
              <SearchSelect label="Status" name="status"
                options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                value={form.status}
                onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button className="add-btn" onClick={handleSubmit}>{isEdit ? 'Update' : 'Submit'}</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHistory} onHide={() => setShowHistory(false)} size="lg" backdrop="static">
        <Modal.Header closeButton><Modal.Title>Tenant History</Modal.Title></Modal.Header>
        <Modal.Body>
          <ResponsiveTable headers={historyHeaders} data={historyData} dataType="TenantHistory" noCheckbox={true} pageSize={10} />
        </Modal.Body>
        <Modal.Footer><Button variant="secondary" onClick={() => setShowHistory(false)}>Close</Button></Modal.Footer>
      </Modal>
    </>
  );
};

export default TenantMaster;
